import React, {
    useState,
    useEffect,
    useCallback,
    useMemo
} from "react";

import {
    Table,
    Card,
    Input,
    AutoComplete,
    Button,
    Tag,
    Typography,
    Spin,
    Empty,
    Row,
    Col,
    Divider,
    Statistic,
    Alert,
    Space,
    Tooltip
} from "antd";

import {
    FaSearch,
    FaSyncAlt,
    FaFilePdf,
    FaDollarSign,
    FaExchangeAlt,
    FaUser,
    FaCalendarAlt,
    FaTimesCircle,
    FaFilter,
    FaArrowUp,
    FaWallet
} from "react-icons/fa";

import {
    PDFDownloadLink
} from "@react-pdf/renderer";

import api from "../../api/api";

import DailyPaymentTransactionPDF
    from "./DailyPaymentTransactionPDF";

import {
    useAcademicYear
} from "../../AcademicYearContext";

const {
    Title,
    Text
} = Typography;

const ORANGE = "#f97316";
const ORANGE_DARK = "#ea580c";
const NAVY = "#0f2742";
const GREEN = "#16a34a";
const RED = "#dc2626";
const GOLD = "#d97706";
const SLATE = "#64748b";

const formatMoney = (value) => {
    const number = Number(value || 0);

    return `$${number.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}`;
};

const formatDate = (value) => {
    if (!value) {
        return "-";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric"
    });
};

const formatDateTime = (value) => {
    if (!value) {
        return "-";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit"
    });
};

const getStudentId = (item) => {
    if (!item) {
        return null;
    }

    if (
        typeof item.student === "object" &&
        item.student !== null
    ) {
        return (
            item.student?.id ||
            item.student_id ||
            null
        );
    }

    return (
        item.student_id ||
        item.student ||
        null
    );
};

const getStudentName = (item) => {
    if (!item) {
        return "-";
    }

    return (
        item.student_name ||
        item.student?.full_name ||
        item.student?.name ||
        item.full_name ||
        item.name ||
        "-"
    );
};

const getPayerName = (item) => {
    return (
        item?.payer_name ||
        item?.payer?.full_name ||
        item?.payer?.name ||
        "-"
    );
};

const getCashierName = (item) => {
    return (
        item?.created_by_name ||
        item?.created_by?.full_name ||
        item?.created_by?.username ||
        item?.created_by ||
        "-"
    );
};

const getBalance = (item) => {
    const value =
        item?.balance ??
        item?.remaining_balance ??
        item?.outstanding_balance ??
        0;

    return Number(value || 0);
};

const getPaymentCategoryClass = (category) => {
    if (!category) {
        return "other";
    }

    const value = String(category).toLowerCase();

    if (value.includes("school")) {
        return "school";
    }

    if (
        value.includes("registration") ||
        value.includes("admission")
    ) {
        return "registration";
    }

    if (
        value.includes("uniform") ||
        value.includes("book") ||
        value.includes("transport")
    ) {
        return "service";
    }

    return "other";
};

const getInitial = (name) => {
    if (!name) {
        return "S";
    }

    return String(name)
        .trim()
        .charAt(0)
        .toUpperCase();
};

const DailyPaymentTransaction = () => {
    const {
        selectedYear
    } = useAcademicYear();

    const [transactions, setTransactions] = useState([]);
    const [studentTransactions, setStudentTransactions] =
        useState([]);
    const [students, setStudents] = useState([]);
    const [selectedStudent, setSelectedStudent] =
        useState(null);

    const [studentSearch, setStudentSearch] =
        useState("");
    const [searchText, setSearchText] =
        useState("");

    const [loading, setLoading] = useState(false);
    const [studentLoading, setStudentLoading] =
        useState(false);

    const [error, setError] = useState("");
    const [studentError, setStudentError] =
        useState("");

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20
    });

    const [totalRecords, setTotalRecords] =
        useState(0);

    const fetchTransactions = useCallback(
        async () => {
            if (!selectedYear?.id) {
                setTransactions([]);
                setTotalRecords(0);
                return;
            }

            try {
                setLoading(true);
                setError("");

                const response = await api.get(
                    "/daily-payment-transactions/",
                    {
                        params: {
                            academic_year:
                                selectedYear.id,
                            daily: true,
                            page:
                                pagination.current,
                            page_size:
                                pagination.pageSize,
                            search:
                                searchText.trim()
                        }
                    }
                );

                const result =
                    response?.data || {};

                const dailyResults =
                    Array.isArray(result)
                        ? result
                        : Array.isArray(
                              result.results
                          )
                            ? result.results
                            : [];

                setTransactions(
                    dailyResults
                );

                setTotalRecords(
                    Array.isArray(result)
                        ? dailyResults.length
                        : Number(
                              result.count || 0
                          )
                );
            } catch (requestError) {
                console.error(
                    "Daily transaction loading error:",
                    requestError
                );

                setTransactions([]);
                setTotalRecords(0);

                setError(
                    requestError?.response?.data
                        ?.detail ||
                        requestError?.response
                            ?.data?.message ||
                        "Unable to load today's payment transactions."
                );
            } finally {
                setLoading(false);
            }
        },
        [
            selectedYear?.id,
            pagination.current,
            pagination.pageSize,
            searchText
        ]
    );

    const fetchStudentTransactions =
        useCallback(
            async (studentId) => {
                if (
                    !studentId ||
                    !selectedYear?.id
                ) {
                    setStudentTransactions(
                        []
                    );
                    return;
                }

                try {
                    setStudentLoading(
                        true
                    );
                    setStudentError("");

                    const response =
                        await api.get(
                            "/daily-payment-transactions/",
                            {
                                params: {
                                    academic_year:
                                        selectedYear.id,
                                    student:
                                        studentId,
                                    page: 1,
                                    page_size: 100
                                }
                            }
                        );

                    const result =
                        response?.data || {};

                    const records =
                        Array.isArray(result)
                            ? result
                            : Array.isArray(
                                  result.results
                              )
                                ? result.results
                                : [];

                    setStudentTransactions(
                        records
                    );
                } catch (
                    requestError
                ) {
                    console.error(
                        "Student transaction loading error:",
                        requestError
                    );

                    setStudentTransactions(
                        []
                    );

                    setStudentError(
                        requestError
                            ?.response
                            ?.data?.detail ||
                            requestError
                                ?.response
                                ?.data
                                ?.message ||
                            "Unable to load this student's payment history."
                    );
                } finally {
                    setStudentLoading(
                        false
                    );
                }
            },
            [selectedYear?.id]
        );

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    useEffect(() => {
        if (!selectedStudent?.id) {
            return;
        }

        fetchStudentTransactions(
            selectedStudent.id
        );
    }, [
        selectedYear?.id,
        selectedStudent?.id,
        fetchStudentTransactions
    ]);

    const fetchPaidStudents =
        useCallback(
            async (value) => {
                setStudentSearch(value);

                if (!value?.trim()) {
                    setStudents([]);
                    return;
                }

                if (!selectedYear?.id) {
                    setStudents([]);
                    return;
                }

                try {
                    const response =
                        await api.get(
                            "/daily-payment-transactions/",
                            {
                                params: {
                                    academic_year:
                                        selectedYear.id,
                                    daily: true,
                                    search:
                                        value.trim(),
                                    page: 1,
                                    page_size: 50
                                }
                            }
                        );

                    const result =
                        response?.data || {};

                    const data =
                        Array.isArray(result)
                            ? result
                            : Array.isArray(
                                  result.results
                              )
                                ? result.results
                                : [];

                    const uniqueStudents =
                        Array.from(
                            new Map(
                                data
                                    .map(
                                        (
                                            item
                                        ) => {
                                            const id =
                                                getStudentId(
                                                    item
                                                );

                                            if (
                                                !id
                                            ) {
                                                return null;
                                            }

                                            return [
                                                String(
                                                    id
                                                ),
                                                {
                                                    id,
                                                    full_name:
                                                        getStudentName(
                                                            item
                                                        )
                                                }
                                            ];
                                        }
                                    )
                                    .filter(
                                        Boolean
                                    )
                            ).values()
                        );

                    setStudents(
                        uniqueStudents
                    );
                } catch (
                    requestError
                ) {
                    console.error(
                        "Student search error:",
                        requestError
                    );

                    setStudents([]);
                }
            },
            [selectedYear?.id]
        );

    const handleStudentSelect =
        useCallback(
            (studentId) => {
                const student =
                    students.find(
                        (item) =>
                            String(
                                item.id
                            ) ===
                            String(
                                studentId
                            )
                    );

                if (!student) {
                    return;
                }

                setSelectedStudent(
                    student
                );

                setStudentSearch(
                    student.full_name
                );

                fetchStudentTransactions(
                    student.id
                );
            },
            [
                students,
                fetchStudentTransactions
            ]
        );

    const clearSelectedStudent =
        useCallback(() => {
            setSelectedStudent(null);
            setStudentSearch("");
            setStudentTransactions([]);
            setStudentError("");
            setStudents([]);
        }, []);

    const handleTableChange = (
        tablePagination
    ) => {
        setPagination({
            current:
                tablePagination.current,
            pageSize:
                tablePagination.pageSize
        });
    };

    const handleSearch = (value) => {
        setSearchText(value);

        setPagination((previous) => ({
            ...previous,
            current: 1
        }));
    };

    const statistics = useMemo(() => {
        const totalAmount =
            transactions.reduce(
                (sum, item) =>
                    sum +
                    Number(
                        item?.amount_paid ||
                            0
                    ),
                0
            );

        const totalBalance =
            transactions.reduce(
                (sum, item) =>
                    sum +
                    getBalance(item),
                0
            );

        const uniqueStudents =
            new Set(
                transactions
                    .map((item) =>
                        getStudentId(item)
                    )
                    .filter(Boolean)
            ).size;

        return {
            totalAmount,
            totalBalance,
            transactionCount:
                transactions.length,
            uniqueStudents
        };
    }, [transactions]);

    const columns = useMemo(
        () => [
            {
                title: "#",
                key: "index",
                width: 60,
                responsive: ["md"],
                render: (_, __, index) =>
                    (pagination.current -
                        1) *
                        pagination.pageSize +
                    index +
                    1
            },
            {
                title: "Student",
                dataIndex:
                    "student_name",
                key: "student_name",
                width: 240,
                fixed: "left",
                render: (
                    _,
                    record
                ) => (
                    <div className="transaction-student-cell">
                        <div className="transaction-avatar">
                            {getInitial(
                                getStudentName(
                                    record
                                )
                            )}
                        </div>

                        <div>
                            <Text
                                strong
                                className="transaction-student-name"
                            >
                                {getStudentName(
                                    record
                                )}
                            </Text>

                            {getStudentId(
                                record
                            ) && (
                                <Text
                                    type="secondary"
                                    className="transaction-student-id"
                                >
                                    ID:{" "}
                                    {getStudentId(
                                        record
                                    )}
                                </Text>
                            )}
                        </div>
                    </div>
                )
            },
            {
                title: "Fee Type",
                dataIndex: "fee_type",
                key: "fee_type",
                width: 180,
                ellipsis: true,
                render: (
                    value
                ) => (
                    <Text>
                        {value || "-"}
                    </Text>
                )
            },
            {
                title: "Category",
                dataIndex:
                    "payment_category",
                key: "payment_category",
                width: 150,
                render: (
                    value
                ) => {
                    const type =
                        getPaymentCategoryClass(
                            value
                        );

                    return (
                        <Tag
                            className={`payment-category-tag ${type}`}
                        >
                            {value ||
                                "Other"}
                        </Tag>
                    );
                }
            },
            {
                title: "Amount Paid",
                dataIndex:
                    "amount_paid",
                key: "amount_paid",
                width: 145,
                align: "right",
                render: (
                    value
                ) => (
                    <Text
                        strong
                        className="payment-amount"
                    >
                        {formatMoney(
                            value
                        )}
                    </Text>
                )
            },
            {
                title: "Balance",
                key: "balance",
                width: 145,
                align: "right",
                render: (
                    _,
                    record
                ) => {
                    const balance =
                        getBalance(
                            record
                        );

                    return (
                        <Text
                            strong
                            className={
                                balance >
                                0
                                    ? "payment-balance"
                                    : "payment-paid"
                            }
                        >
                            {formatMoney(
                                balance
                            )}
                        </Text>
                    );
                }
            },
            {
                title: "Payer",
                key: "payer_name",
                width: 170,
                responsive: ["lg"],
                ellipsis: true,
                render: (
                    _,
                    record
                ) =>
                    getPayerName(
                        record
                    )
            },
            {
                title: "Cashier",
                key: "created_by",
                width: 150,
                responsive: ["lg"],
                ellipsis: true,
                render: (
                    _,
                    record
                ) =>
                    getCashierName(
                        record
                    )
            },
            {
                title: "Date",
                key: "created_at",
                width: 160,
                render: (
                    _,
                    record
                ) => (
                    <div className="transaction-date-cell">
                        <Text>
                            {formatDate(
                                record?.created_at ||
                                    record?.date
                            )}
                        </Text>

                        {record?.created_at && (
                            <Text type="secondary">
                                {new Date(
                                    record.created_at
                                ).toLocaleTimeString(
                                    "en-US",
                                    {
                                        hour: "numeric",
                                        minute:
                                            "2-digit"
                                    }
                                )}
                            </Text>
                        )}
                    </div>
                )
            },
            {
                title: "Academic Year",
                dataIndex:
                    "academic_year",
                key: "academic_year",
                width: 140,
                responsive: ["xl"],
                render: (
                    value
                ) =>
                    typeof value ===
                    "object"
                        ? value?.name ||
                          "-"
                        : value ||
                          "-"
            }
        ],
        [
            pagination.current,
            pagination.pageSize
        ]
    );

    const studentOptions =
        useMemo(
            () =>
                students.map(
                    (
                        student
                    ) => ({
                        value: String(
                            student.id
                        ),
                        label: (
                            <div className="student-search-option">
                                <div className="student-search-avatar">
                                    {getInitial(
                                        student.full_name
                                    )}
                                </div>

                                <div>
                                    <Text strong>
                                        {
                                            student.full_name
                                        }
                                    </Text>

                                    <Text
                                        type="secondary"
                                        className="student-search-id"
                                    >
                                        Student ID:{" "}
                                        {
                                            student.id
                                        }
                                    </Text>
                                </div>
                            </div>
                        )
                    })
                ),
            [students]
        );

    const MobileTransactionCard =
        ({ item }) => {
            const balance =
                getBalance(item);

            return (
                <Card
                    className="mobile-payment-card"
                    bordered={false}
                >
                    <div className="mobile-card-top">
                        <div className="transaction-student-cell">
                            <div className="transaction-avatar">
                                {getInitial(
                                    getStudentName(
                                        item
                                    )
                                )}
                            </div>

                            <div>
                                <Text strong>
                                    {getStudentName(
                                        item
                                    )}
                                </Text>

                                <Text
                                    type="secondary"
                                    className="mobile-id"
                                >
                                    ID:{" "}
                                    {getStudentId(
                                        item
                                    ) ||
                                        "-"}
                                </Text>
                            </div>
                        </div>

                        <Text
                            strong
                            className="mobile-payment-amount"
                        >
                            {formatMoney(
                                item?.amount_paid
                            )}
                        </Text>
                    </div>

                    <Divider />

                    <div className="mobile-payment-grid">
                        <div>
                            <Text type="secondary">
                                Fee Type
                            </Text>

                            <Text strong>
                                {item?.fee_type ||
                                    "-"}
                            </Text>
                        </div>

                        <div>
                            <Text type="secondary">
                                Category
                            </Text>

                            <Tag
                                className={`payment-category-tag ${getPaymentCategoryClass(
                                    item?.payment_category
                                )}`}
                            >
                                {item?.payment_category ||
                                    "Other"}
                            </Tag>
                        </div>

                        <div>
                            <Text type="secondary">
                                Balance
                            </Text>

                            <Text
                                strong
                                className={
                                    balance >
                                    0
                                        ? "payment-balance"
                                        : "payment-paid"
                                }
                            >
                                {formatMoney(
                                    balance
                                )}
                            </Text>
                        </div>

                        <div>
                            <Text type="secondary">
                                Date
                            </Text>

                            <Text strong>
                                {formatDate(
                                    item?.created_at ||
                                        item?.date
                                )}
                            </Text>
                        </div>

                        <div>
                            <Text type="secondary">
                                Payer
                            </Text>

                            <Text strong>
                                {getPayerName(
                                    item
                                )}
                            </Text>
                        </div>

                        <div>
                            <Text type="secondary">
                                Cashier
                            </Text>

                            <Text strong>
                                {getCashierName(
                                    item
                                )}
                            </Text>
                        </div>
                    </div>
                </Card>
            );
        };

    return (
        <div className="daily-payment-page">
            <style>
                {`
                    * {
                        box-sizing: border-box;
                    }

                    .daily-payment-page {
                        min-height: 100vh;
                        padding: 24px;
                        background:
                            radial-gradient(
                                circle at top right,
                                rgba(249, 115, 22, 0.08),
                                transparent 32%
                            ),
                            linear-gradient(
                                180deg,
                                #f7f9fc 0%,
                                #eef2f6 100%
                            );
                    }

                    .daily-payment-container {
                        width: 100%;
                        max-width: 1700px;
                        margin: 0 auto;
                    }

                    .daily-payment-page .ant-card {
                        border-radius: 18px;
                    }

                    .daily-payment-hero {
                        position: relative;
                        overflow: hidden;
                        margin-bottom: 18px;
                        padding: 8px;
                        background:
                            linear-gradient(
                                135deg,
                                #0f172a 0%,
                                #172554 50%,
                                #0f2742 100%
                            );
                        box-shadow:
                            0 18px 45px
                            rgba(15, 39, 66, 0.16);
                    }

                    .daily-payment-hero
                        .ant-card-body {
                        position: relative;
                        z-index: 2;
                        padding: 30px;
                    }

                    .hero-background-shape {
                        position: absolute;
                        border-radius: 50%;
                        pointer-events: none;
                        z-index: 1;
                    }

                    .hero-shape-one {
                        width: 280px;
                        height: 280px;
                        right: -90px;
                        top: -130px;
                        background:
                            rgba(
                                249,
                                115,
                                22,
                                0.18
                            );
                    }

                    .hero-shape-two {
                        width: 190px;
                        height: 190px;
                        right: 180px;
                        bottom: -130px;
                        background:
                            rgba(
                                255,
                                255,
                                255,
                                0.05
                            );
                    }

                    .hero-icon {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: 52px;
                        height: 52px;
                        margin-bottom: 15px;
                        border-radius: 15px;
                        color: #fff;
                        font-size: 23px;
                        background:
                            linear-gradient(
                                135deg,
                                #f97316,
                                #ea580c
                            );
                        box-shadow:
                            0 10px 25px
                            rgba(
                                249,
                                115,
                                22,
                                0.3
                            );
                    }

                    .hero-eyebrow,
                    .section-eyebrow {
                        display: block;
                        margin-bottom: 5px;
                        color: #fb923c;
                        font-size: 11px;
                        font-weight: 800;
                        letter-spacing: 1.6px;
                    }

                    .hero-title {
                        margin: 0 !important;
                        color: #fff !important;
                        font-size: 30px !important;
                        font-weight: 800 !important;
                    }

                    .hero-description {
                        display: block;
                        max-width: 700px;
                        margin-top: 9px;
                        color:
                            rgba(
                                255,
                                255,
                                255,
                                0.72
                            );
                        font-size: 14px;
                        line-height: 1.7;
                    }

                    .academic-year-panel {
                        padding: 19px 20px;
                        border: 1px solid
                            rgba(
                                255,
                                255,
                                255,
                                0.12
                            );
                        border-radius: 16px;
                        background:
                            rgba(
                                255,
                                255,
                                255,
                                0.07
                            );
                        backdrop-filter: blur(10px);
                    }

                    .academic-year-panel
                        > span {
                        color:
                            rgba(
                                255,
                                255,
                                255,
                                0.55
                            );
                        font-size: 10px;
                        font-weight: 800;
                        letter-spacing: 1.2px;
                    }

                    .academic-year-value {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        margin-top: 10px;
                        color: #fff;
                        font-size: 18px;
                        font-weight: 800;
                    }

                    .academic-year-value svg {
                        color: #fb923c;
                    }

                    .payment-stat-row {
                        margin-bottom: 18px;
                    }

                    .payment-stat-card {
                        height: 100%;
                        border: 1px solid
                            #e8edf3;
                        background: #fff;
                        box-shadow:
                            0 8px 24px
                            rgba(
                                15,
                                23,
                                42,
                                0.055
                            );
                    }

                    .payment-stat-card
                        .ant-card-body {
                        padding: 20px;
                    }

                    .payment-stat-card
                        .ant-statistic-title {
                        margin-bottom: 8px;
                        color: #64748b;
                        font-size: 12px;
                        font-weight: 700;
                    }

                    .payment-stat-card
                        .ant-statistic-content {
                        color: #0f172a;
                        font-size: 26px;
                        font-weight: 800;
                    }

                    .payment-stat-card
                        .ant-statistic-content-prefix {
                        margin-right: 8px;
                        color: ${ORANGE};
                    }

                    .stat-footer {
                        display: flex;
                        align-items: center;
                        gap: 7px;
                        margin-top: 13px;
                        color: #64748b;
                        font-size: 11px;
                        font-weight: 600;
                    }

                    .stat-footer svg {
                        color: ${ORANGE};
                    }

                    .stat-footer.green svg {
                        color: ${GREEN};
                    }

                    .stat-footer.warning svg {
                        color: ${GOLD};
                    }

                    .payment-workspace-card {
                        border: 1px solid
                            #e8edf3;
                        background: #fff;
                        box-shadow:
                            0 10px 32px
                            rgba(
                                15,
                                23,
                                42,
                                0.06
                            );
                    }

                    .payment-workspace-card
                        .ant-card-body {
                        padding: 25px;
                    }

                    .workspace-header {
                        display: flex;
                        align-items: flex-start;
                        justify-content: space-between;
                        gap: 20px;
                    }

                    .workspace-title {
                        margin: 2px 0 5px !important;
                        color: #0f172a !important;
                        font-weight: 800 !important;
                    }

                    .workspace-subtitle {
                        display: block;
                        max-width: 700px;
                        font-size: 13px;
                        line-height: 1.6;
                    }

                    .workspace-actions {
                        flex-shrink: 0;
                    }

                    .workspace-actions
                        .ant-btn {
                        height: 40px;
                        border-radius: 10px;
                        font-weight: 700;
                    }

                    .pdf-export-button {
                        border: none !important;
                        background:
                            linear-gradient(
                                135deg,
                                #f97316,
                                #ea580c
                            ) !important;
                        box-shadow:
                            0 8px 18px
                            rgba(
                                249,
                                115,
                                22,
                                0.22
                            );
                    }

                    .payment-filter-area {
                        padding: 17px;
                        border: 1px solid
                            #edf1f5;
                        border-radius: 14px;
                        background: #f8fafc;
                    }

                    .payment-filter-area
                        .ant-input-affix-wrapper {
                        border-radius: 10px;
                    }

                    .student-autocomplete {
                        width: 100%;
                    }

                    .student-autocomplete
                        .ant-input-affix-wrapper {
                        width: 100%;
                    }

                    .filter-summary {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 9px;
                        min-height: 40px;
                        padding: 0 14px;
                        border: 1px solid
                            #e2e8f0;
                        border-radius: 10px;
                        color: #475569;
                        background: #fff;
                        font-size: 12px;
                        font-weight: 700;
                    }

                    .filter-summary svg {
                        color: ${ORANGE};
                    }

                    .student-search-option {
                        display: flex;
                        align-items: center;
                        gap: 11px;
                        padding: 4px 0;
                    }

                    .student-search-avatar,
                    .transaction-avatar {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        flex-shrink: 0;
                        border-radius: 50%;
                        color: #fff;
                        font-weight: 800;
                        background:
                            linear-gradient(
                                135deg,
                                #0f2742,
                                #1e3a5f
                            );
                    }

                    .student-search-avatar {
                        width: 34px;
                        height: 34px;
                        font-size: 12px;
                    }

                    .student-search-id {
                        display: block;
                        font-size: 11px;
                    }

                    .payment-alert {
                        margin-top: 16px;
                        border-radius: 12px;
                    }

                    .selected-student-panel {
                        margin-top: 18px;
                        border: 1px solid
                            #fed7aa !important;
                        background:
                            linear-gradient(
                                135deg,
                                #fffaf5,
                                #fff
                            ) !important;
                    }

                    .selected-student-panel
                        .ant-card-body {
                        padding: 20px;
                    }

                    .selected-student-header {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 16px;
                    }

                    .selected-student-info {
                        display: flex;
                        align-items: center;
                        gap: 14px;
                    }

                    .large-student-avatar {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: 58px;
                        height: 58px;
                        border-radius: 16px;
                        color: #fff;
                        font-size: 21px;
                        font-weight: 800;
                        background:
                            linear-gradient(
                                135deg,
                                #0f2742,
                                #1e3a5f
                            );
                        box-shadow:
                            0 8px 18px
                            rgba(
                                15,
                                39,
                                66,
                                0.15
                            );
                    }

                    .selected-label {
                        display: block;
                        color: ${ORANGE_DARK};
                        font-size: 10px;
                        font-weight: 800;
                        letter-spacing: 1.2px;
                    }

                    .selected-student-name {
                        margin: 2px 0 !important;
                        color: #0f172a !important;
                        font-weight: 800 !important;
                    }

                    .student-history-alert {
                        margin-top: 16px;
                    }

                    .student-history-loading {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 10px;
                        min-height: 100px;
                    }

                    .student-history-list {
                        margin-top: 18px;
                        overflow: hidden;
                        border: 1px solid
                            #edf1f5;
                        border-radius: 12px;
                        background: #fff;
                    }

                    .student-history-row {
                        display: grid;
                        grid-template-columns:
                            minmax(180px, 1.4fr)
                            minmax(110px, 0.7fr)
                            minmax(120px, 0.6fr);
                        align-items: center;
                        gap: 15px;
                        padding: 14px 16px;
                        border-bottom: 1px solid
                            #edf1f5;
                    }

                    .student-history-row:last-child {
                        border-bottom: none;
                    }

                    .history-date {
                        display: block;
                        margin-top: 4px;
                        font-size: 11px;
                    }

                    .history-category {
                        text-align: center;
                    }

                    .transaction-content {
                        margin-top: 22px;
                    }

                    .desktop-payment-table {
                        width: 100%;
                    }

                    .desktop-payment-table
                        .ant-table-wrapper {
                        border: 1px solid
                            #edf1f5;
                        border-radius: 14px;
                        overflow: hidden;
                    }

                    .desktop-payment-table
                        .ant-table {
                        background: #fff;
                    }

                    .desktop-payment-table
                        .ant-table-thead
                        > tr
                        > th {
                        padding-top: 14px;
                        padding-bottom: 14px;
                        color: #475569;
                        background: #f8fafc;
                        border-bottom: 1px solid
                            #e2e8f0;
                        font-size: 11px;
                        font-weight: 800;
                        letter-spacing: 0.3px;
                        text-transform: uppercase;
                    }

                    .professional-payment-row
                        > td {
                        padding-top: 14px !important;
                        padding-bottom: 14px !important;
                        border-bottom: 1px solid
                            #f1f5f9 !important;
                    }

                    .professional-payment-row:hover
                        > td {
                        background: #fffaf5 !important;
                    }

                    .transaction-student-cell {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        min-width: 0;
                    }

                    .transaction-avatar {
                        width: 38px;
                        height: 38px;
                        font-size: 12px;
                    }

                    .transaction-student-name {
                        display: block;
                        max-width: 170px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        color: #172033;
                    }

                    .transaction-student-id {
                        display: block;
                        margin-top: 2px;
                        font-size: 10px;
                    }

                    .transaction-date-cell {
                        display: flex;
                        flex-direction: column;
                        gap: 2px;
                    }

                    .transaction-date-cell
                        .ant-typography-secondary {
                        font-size: 11px;
                    }

                    .payment-amount {
                        color: ${GREEN} !important;
                    }

                    .payment-balance {
                        color: ${RED} !important;
                    }

                    .payment-paid {
                        color: ${GREEN} !important;
                    }

                    .payment-category-tag {
                        margin: 0;
                        padding: 3px 9px;
                        border: none !important;
                        border-radius: 999px;
                        font-size: 10px;
                        font-weight: 800;
                    }

                    .payment-category-tag.school {
                        color: #9a3412;
                        background: #ffedd5;
                    }

                    .payment-category-tag.registration {
                        color: #1d4ed8;
                        background: #dbeafe;
                    }

                    .payment-category-tag.service {
                        color: #166534;
                        background: #dcfce7;
                    }

                    .payment-category-tag.other {
                        color: #475569;
                        background: #e2e8f0;
                    }

                    .payment-loading {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        gap: 12px;
                        min-height: 280px;
                    }

                    .payment-empty {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        min-height: 280px;
                        border: 1px dashed
                            #dbe2ea;
                        border-radius: 14px;
                        background: #fafbfd;
                    }

                    .mobile-payment-list {
                        display: none;
                    }

                    .mobile-payment-card {
                        margin-bottom: 12px;
                        border: 1px solid
                            #e7ebf0;
                        box-shadow:
                            0 7px 20px
                            rgba(
                                15,
                                23,
                                42,
                                0.05
                            );
                    }

                    .mobile-payment-card
                        .ant-card-body {
                        padding: 16px;
                    }

                    .mobile-card-top {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 12px;
                    }

                    .mobile-payment-amount {
                        flex-shrink: 0;
                        color: ${GREEN};
                        font-size: 16px;
                    }

                    .mobile-id {
                        display: block;
                        margin-top: 3px;
                        font-size: 10px;
                    }

                    .mobile-payment-grid {
                        display: grid;
                        grid-template-columns:
                            repeat(
                                2,
                                minmax(
                                    0,
                                    1fr
                                )
                            );
                        gap: 15px;
                    }

                    .mobile-payment-grid
                        > div {
                        display: flex;
                        flex-direction: column;
                        gap: 4px;
                        min-width: 0;
                    }

                    .mobile-payment-grid
                        .ant-typography-secondary {
                        font-size: 10px;
                        font-weight: 700;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                    }

                    .mobile-action-text {
                        display: none;
                    }

                    @media (max-width: 900px) {
                        .daily-payment-page {
                            padding: 16px;
                        }

                        .daily-payment-hero
                            .ant-card-body {
                            padding: 24px;
                        }

                        .hero-title {
                            font-size: 26px !important;
                        }

                        .workspace-header {
                            flex-direction: column;
                        }

                        .workspace-actions {
                            width: 100%;
                        }

                        .workspace-actions
                            .ant-btn {
                            flex: 1;
                        }

                        .student-history-row {
                            grid-template-columns:
                                minmax(
                                    160px,
                                    1fr
                                )
                                minmax(
                                    100px,
                                    0.7fr
                                )
                                minmax(
                                    110px,
                                    0.6fr
                                );
                        }
                    }

                    @media (max-width: 767px) {
                        .daily-payment-page {
                            padding: 10px;
                        }

                        .daily-payment-hero {
                            margin-bottom: 12px;
                            border-radius: 15px;
                        }

                        .daily-payment-hero
                            .ant-card-body {
                            padding: 20px;
                        }

                        .hero-title {
                            font-size: 23px !important;
                            line-height: 1.2 !important;
                        }

                        .hero-description {
                            font-size: 12px;
                        }

                        .academic-year-panel {
                            margin-top: 4px;
                        }

                        .payment-stat-card
                            .ant-card-body {
                            padding: 16px;
                        }

                        .payment-stat-card
                            .ant-statistic-content {
                            font-size: 23px;
                        }

                        .payment-workspace-card
                            .ant-card-body {
                            padding: 16px;
                        }

                        .workspace-title {
                            font-size: 20px !important;
                        }

                        .workspace-subtitle {
                            font-size: 12px;
                        }

                        .workspace-actions {
                            display: grid !important;
                            grid-template-columns:
                                1fr 1fr;
                            width: 100%;
                        }

                        .workspace-actions
                            .ant-space-item {
                            width: 100%;
                        }

                        .workspace-actions
                            .ant-btn {
                            width: 100%;
                        }

                        .desktop-action-text {
                            display: none;
                        }

                        .mobile-action-text {
                            display: inline;
                        }

                        .payment-filter-area {
                            padding: 12px;
                        }

                        .filter-summary {
                            justify-content: flex-start;
                        }

                        .selected-student-header {
                            align-items: flex-start;
                        }

                        .selected-student-info {
                            min-width: 0;
                        }

                        .large-student-avatar {
                            width: 48px;
                            height: 48px;
                            border-radius: 13px;
                        }

                        .selected-student-name {
                            max-width: 190px;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                            font-size: 18px !important;
                        }

                        .student-history-row {
                            grid-template-columns: 1fr;
                            gap: 9px;
                        }

                        .history-category {
                            text-align: left;
                        }

                        .desktop-payment-table {
                            display: none;
                        }

                        .mobile-payment-list {
                            display: block;
                        }

                        .mobile-payment-grid {
                            grid-template-columns:
                                1fr 1fr;
                        }

                        .transaction-avatar {
                            width: 34px;
                            height: 34px;
                        }

                        .transaction-student-name {
                            max-width: 140px;
                        }
                    }

                    @media (max-width: 430px) {
                        .workspace-actions {
                            grid-template-columns:
                                1fr;
                        }

                        .mobile-payment-grid {
                            grid-template-columns:
                                1fr;
                        }

                        .selected-student-header {
                            gap: 8px;
                        }

                        .selected-student-header
                            .ant-btn {
                            padding-inline: 5px;
                        }

                        .selected-student-name {
                            max-width: 150px;
                        }

                        .hero-title {
                            font-size: 21px !important;
                        }
                    }
                `}
            </style>

            <div className="daily-payment-container">
                <Card
                    className="daily-payment-hero"
                    bordered={false}
                >
                    <div className="hero-background-shape hero-shape-one" />
                    <div className="hero-background-shape hero-shape-two" />

                    <Row
                        align="middle"
                        justify="space-between"
                        gutter={[
                            24,
                            24
                        ]}
                    >
                        <Col
                            xs={24}
                            lg={16}
                        >
                            <div className="hero-icon">
                                <FaWallet
                                    size={22}
                                />
                            </div>

                            <Text className="hero-eyebrow">
                                FINANCE & ACCOUNTING
                            </Text>

                            <Title
                                level={2}
                                className="hero-title"
                            >
                                Daily Payment Transactions
                            </Title>

                            <Text className="hero-description">
                                Monitor, search, and review
                                payment activity recorded
                                for the current academic year.
                            </Text>
                        </Col>

                        <Col
                            xs={24}
                            lg={8}
                        >
                            <div className="academic-year-panel">
                                <Text>
                                    ACTIVE ACADEMIC YEAR
                                </Text>

                                <div className="academic-year-value">
                                    <FaCalendarAlt />

                                    <span>
                                        {selectedYear?.name ||
                                            "No Active Year"}
                                    </span>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card>

                <Row
                    gutter={[16, 16]}
                    className="payment-stat-row"
                >
                    <Col
                        xs={24}
                        sm={12}
                        xl={6}
                    >
                        <Card
                            className="payment-stat-card"
                            bordered={false}
                        >
                            <Statistic
                                title="Today's Transactions"
                                value={
                                    totalRecords
                                }
                                prefix={
                                    <FaExchangeAlt />
                                }
                            />

                            <div className="stat-footer">
                                <FaArrowUp />

                                <span>
                                    Recorded payment entries
                                </span>
                            </div>
                        </Card>
                    </Col>

                    <Col
                        xs={24}
                        sm={12}
                        xl={6}
                    >
                        <Card
                            className="payment-stat-card"
                            bordered={false}
                        >
                            <Statistic
                                title="Amount on Current Page"
                                value={
                                    statistics.totalAmount
                                }
                                precision={2}
                                prefix="$"
                            />

                            <div className="stat-footer green">
                                <FaDollarSign />

                                <span>
                                    Payments received
                                </span>
                            </div>
                        </Card>
                    </Col>

                    <Col
                        xs={24}
                        sm={12}
                        xl={6}
                    >
                        <Card
                            className="payment-stat-card"
                            bordered={false}
                        >
                            <Statistic
                                title="Outstanding Balance"
                                value={
                                    statistics.totalBalance
                                }
                                precision={2}
                                prefix="$"
                            />

                            <div className="stat-footer warning">
                                <FaWallet />

                                <span>
                                    Balance on current page
                                </span>
                            </div>
                        </Card>
                    </Col>

                    <Col
                        xs={24}
                        sm={12}
                        xl={6}
                    >
                        <Card
                            className="payment-stat-card"
                            bordered={false}
                        >
                            <Statistic
                                title="Students Served"
                                value={
                                    statistics.uniqueStudents
                                }
                                prefix={
                                    <FaUser />
                                }
                            />

                            <div className="stat-footer">
                                <FaUser />

                                <span>
                                    Unique students
                                </span>
                            </div>
                        </Card>
                    </Col>
                </Row>

                <Card
                    className="payment-workspace-card"
                    bordered={false}
                >
                    <div className="workspace-header">
                        <div>
                            <Text className="section-eyebrow">
                                TRANSACTION REGISTER
                            </Text>

                            <Title
                                level={4}
                                className="workspace-title"
                            >
                                Today's Payment Activity
                            </Title>

                            <Text
                                type="secondary"
                                className="workspace-subtitle"
                            >
                                Search the daily payment
                                register or locate an individual
                                student's payment history.
                            </Text>
                        </div>

                        <Space
                            wrap
                            className="workspace-actions"
                        >
                            <Tooltip title="Reload payment transactions">
                                <Button
                                    icon={
                                        <FaSyncAlt />
                                    }
                                    onClick={
                                        fetchTransactions
                                    }
                                    loading={
                                        loading
                                    }
                                >
                                    <span className="desktop-action-text">
                                        Refresh
                                    </span>

                                    <span className="mobile-action-text">
                                        Refresh
                                    </span>
                                </Button>
                            </Tooltip>

                            <PDFDownloadLink
                                document={
                                    <DailyPaymentTransactionPDF
                                        transactions={
                                            transactions
                                        }
                                    />
                                }
                                fileName="Daily_Payment_Record.pdf"
                            >
                                {({
                                    loading:
                                        pdfLoading
                                }) => (
                                    <Button
                                        type="primary"
                                        icon={
                                            <FaFilePdf />
                                        }
                                        disabled={
                                            pdfLoading ||
                                            transactions.length ===
                                                0
                                        }
                                        className="pdf-export-button"
                                    >
                                        Daily PDF
                                    </Button>
                                )}
                            </PDFDownloadLink>
                        </Space>
                    </div>

                    <Divider />

                    <div className="payment-filter-area">
                        <Row
                            gutter={[
                                12,
                                12
                            ]}
                            align="middle"
                        >
                            <Col
                                xs={24}
                                lg={10}
                                xl={9}
                            >
                                <Input
                                    size="large"
                                    allowClear
                                    value={
                                        searchText
                                    }
                                    prefix={
                                        <FaSearch />
                                    }
                                    placeholder="Search student, fee type, payer..."
                                    onChange={(
                                        event
                                    ) =>
                                        handleSearch(
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </Col>

                            <Col
                                xs={24}
                                lg={8}
                                xl={8}
                            >
                                <AutoComplete
                                    className="student-autocomplete"
                                    options={
                                        studentOptions
                                    }
                                    value={
                                        studentSearch
                                    }
                                    onSearch={
                                        fetchPaidStudents
                                    }
                                    onSelect={
                                        handleStudentSelect
                                    }
                                    allowClear
                                    filterOption={
                                        false
                                    }
                                    onClear={
                                        clearSelectedStudent
                                    }
                                    style={{
                                        width:
                                            "100%"
                                    }}
                                >
                                    <Input
                                        size="large"
                                        prefix={
                                            <FaUser />
                                        }
                                        placeholder="Find a student's payment history..."
                                    />
                                </AutoComplete>
                            </Col>

                            <Col
                                xs={24}
                                lg={6}
                                xl={7}
                            >
                                <div className="filter-summary">
                                    <FaFilter />

                                    <span>
                                        {totalRecords.toLocaleString()}
                                        {" "}
                                        transaction
                                        {totalRecords ===
                                        1
                                            ? ""
                                            : "s"}
                                    </span>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    {error && (
                        <Alert
                            type="error"
                            showIcon
                            closable
                            message="Unable to load transactions"
                            description={
                                error
                            }
                            className="payment-alert"
                        />
                    )}

                    {selectedStudent && (
                        <Card
                            className="selected-student-panel"
                            bordered={false}
                        >
                            <div className="selected-student-header">
                                <div className="selected-student-info">
                                    <div className="large-student-avatar">
                                        {getInitial(
                                            selectedStudent.full_name
                                        )}
                                    </div>

                                    <div>
                                        <Text className="selected-label">
                                            STUDENT PAYMENT HISTORY
                                        </Text>

                                        <Title
                                            level={4}
                                            className="selected-student-name"
                                        >
                                            {
                                                selectedStudent.full_name
                                            }
                                        </Title>

                                        <Text type="secondary">
                                            Student ID:{" "}
                                            {
                                                selectedStudent.id
                                            }
                                        </Text>
                                    </div>
                                </div>

                                <Button
                                    type="text"
                                    danger
                                    icon={
                                        <FaTimesCircle />
                                    }
                                    onClick={
                                        clearSelectedStudent
                                    }
                                >
                                    Clear
                                </Button>
                            </div>

                            {studentError && (
                                <Alert
                                    type="error"
                                    showIcon
                                    message={
                                        studentError
                                    }
                                    className="student-history-alert"
                                />
                            )}

                            {studentLoading ? (
                                <div className="student-history-loading">
                                    <Spin />

                                    <Text type="secondary">
                                        Loading student payment history...
                                    </Text>
                                </div>
                            ) : studentTransactions.length ===
                              0 ? (
                                <Empty
                                    image={
                                        Empty.PRESENTED_IMAGE_SIMPLE
                                    }
                                    description="No payment history found for this student."
                                />
                            ) : (
                                <div className="student-history-list">
                                    {studentTransactions.map(
                                        (
                                            item,
                                            index
                                        ) => (
                                            <div
                                                className="student-history-row"
                                                key={
                                                    item.id ||
                                                    `${getStudentId(
                                                        item
                                                    )}-${index}`
                                                }
                                            >
                                                <div>
                                                    <Text strong>
                                                        {item.fee_type ||
                                                            "Payment"}
                                                    </Text>

                                                    <Text
                                                        type="secondary"
                                                        className="history-date"
                                                    >
                                                        {formatDateTime(
                                                            item.created_at ||
                                                                item.date
                                                        )}
                                                    </Text>
                                                </div>

                                                <div className="history-category">
                                                    <Tag
                                                        className={`payment-category-tag ${getPaymentCategoryClass(
                                                            item.payment_category
                                                        )}`}
                                                    >
                                                        {
                                                            item.payment_category ||
                                                            "Other"
                                                        }
                                                    </Tag>
                                                </div>

                                                <Text
                                                    strong
                                                    className="payment-amount"
                                                >
                                                    {formatMoney(
                                                        item.amount_paid
                                                    )}
                                                </Text>
                                            </div>
                                        )
                                    )}
                                </div>
                            )}
                        </Card>
                    )}

                    <div className="transaction-content">
                        {loading ? (
                            <div className="payment-loading">
                                <Spin size="large" />

                                <Text type="secondary">
                                    Loading payment transactions...
                                </Text>
                            </div>
                        ) : transactions.length ===
                          0 ? (
                            <div className="payment-empty">
                                <Empty
                                    image={
                                        Empty.PRESENTED_IMAGE_SIMPLE
                                    }
                                    description={
                                        searchText
                                            ? "No transactions match your search."
                                            : "No payment records found for today."
                                    }
                                />

                                {searchText && (
                                    <Button
                                        type="link"
                                        onClick={() =>
                                            handleSearch(
                                                ""
                                            )
                                        }
                                    >
                                        Clear search
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="desktop-payment-table">
                                    <Table
                                        rowKey="id"
                                        columns={
                                            columns
                                        }
                                        dataSource={
                                            transactions
                                        }
                                        loading={
                                            loading
                                        }
                                        scroll={{
                                            x: 1250
                                        }}
                                        pagination={{
                                            current:
                                                pagination.current,
                                            pageSize:
                                                pagination.pageSize,
                                            total:
                                                totalRecords,
                                            showSizeChanger:
                                                true,
                                            pageSizeOptions:
                                                [
                                                    20,
                                                    50,
                                                    100
                                                ],
                                            showTotal:
                                                (
                                                    total,
                                                    range
                                                ) =>
                                                    `${range[0]}-${range[1]} of ${total} transactions`
                                        }}
                                        onChange={
                                            handleTableChange
                                        }
                                        rowClassName={() =>
                                            "professional-payment-row"
                                        }
                                    />
                                </div>

                                <div className="mobile-payment-list">
                                    {transactions.map(
                                        (
                                            item,
                                            index
                                        ) => (
                                            <MobileTransactionCard
                                                key={
                                                    item.id ||
                                                    index
                                                }
                                                item={
                                                    item
                                                }
                                            />
                                        )
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default DailyPaymentTransaction;